import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSessionState } from '@/hooks/useSessionState';
import { PendingOrdersSection } from '@/modules/supply/components/PendingOrdersSection';
import { supplyService, type PendingOrder } from '@/services/supplyService';
import { invalidateSupplyTableCache } from '@/utils/supplyTableCache';

/** Órdenes pendientes del almacén con el mismo detalle y las mismas acciones (ERP, Excel, descuentos) que la bandeja de trabajo. */
export function LoadEstimatePendingOrders({ warehouseId }: { warehouseId: number }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['pendingOrders', warehouseId],
    queryFn: () => supplyService.getPendingOrders(true, warehouseId),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  // La sección edita las órdenes en local (cantidades, descuentos, borrados) y recarga si algo falla.
  const [orders, setOrders] = useState<PendingOrder[]>([]);
  useEffect(() => {
    if (query.data) setOrders(query.data);
  }, [query.data]);
  const { refetch } = query;
  const reloadOrders = useCallback(async () => {
    const result = await refetch();
    if (result.data) setOrders(result.data);
  }, [refetch]);
  // Misma preferencia de MP/MOQ que la bandeja.
  const [applyMoqMp] = useSessionState<boolean>('supply_applyMoqMp', true);

  // Cancelar o enviar una orden cambia qué proveedores tienen pedido: la tabla del módulo y la caché de la bandeja se actualizan.
  const refreshAfterChange = useCallback(async () => {
    invalidateSupplyTableCache(warehouseId);
    await queryClient.invalidateQueries({ queryKey: ['loadEstimate'] });
  }, [queryClient, warehouseId]);

  return (
    <PendingOrdersSection
      orders={orders}
      setOrders={setOrders}
      reloadOrders={reloadOrders}
      warehouseId={warehouseId}
      applyMoqMp={applyMoqMp}
      onOrderSent={refreshAfterChange}
      onItemsCancelled={() => { void refreshAfterChange(); }}
    />
  );
}
